// Navigation « ligne de temps » : une barre au bas de l'écran. À gauche, l'année et l'emploi du
// moment ; à droite, la ligne du parcours, qu'on glisse comme une vidéo. Module autonome : il pose
// son propre balisage, apporte sa feuille et ne lit que ceci de la page.
//
//   Sections, dans l'ordre de la page : [data-nav] (nom affiché hors emploi), et leur propriété CSS
//   --rail (leur couleur sur la ligne, posée par les classes de couleur de la page).
//   Chapitres : data-chapitre (leur année s'affiche sur la ligne), data-y0 (année de début),
//   data-parallele (le chapitre court en parallèle du précédent : son année se lit « depuis »).
//   Débuts d'emploi : [data-annee][data-entreprise][data-poste], et data-court, le nom affiché
//   (data-entreprise sinon). Le point se pose sur l'élément qui porte ces attributs.
//
// L'année affichée n'est jamais calculée : c'est celle du dernier début d'emploi franchi dans le
// chapitre, sinon l'année de début du chapitre.
//
// Positions : celles du flux de la page, mesurées à la construction (chargement, polices,
// redimensionnement, changement de hauteur de la page). Pendant ces mesures, la page porte la
// classe mesure-flux : un module qui décale des sections (collage, transformations) l'annule sous
// cette classe.
//
// Pour changer de navigation : remplacer l'import de ce module dans main.ts.

import './ligne-de-temps.scss';

/** Écart minimal, en pixels, entre deux points d'emploi sur la ligne. */
const ECART = 15;

/** Point de contrôle de la carte page-ligne : deux fractions, de 0 à 1. */
type Repere = readonly [page: number, ligne: number];
/** Début d'emploi : position dans la page (pixels), année, nom affiché. */
type Debut = readonly [position: number, annee: string, nom: string];

const element = <K extends keyof HTMLElementTagNameMap>(balise: K, classe: string) => {
  const e = document.createElement(balise);
  e.className = classe;
  return e;
};

/**
 * Écarte des positions croissantes (en pixels) d'au moins `g`, en gardant chaque groupe centré
 * sur la moyenne de ses positions d'origine et dans [g / 2, L - g / 2].
 */
function ecarter(xs: readonly number[], g: number, L: number): number[] {
  interface Groupe {
    n: number;
    somme: number;
  }
  const groupes: Groupe[] = xs.map((x) => ({ n: 1, somme: x }));
  const debut = (gr: Groupe) =>
    Math.min(L - g / 2 - (gr.n - 1) * g, Math.max(g / 2, gr.somme / gr.n - ((gr.n - 1) * g) / 2));
  for (let i = 0; i < groupes.length - 1;) {
    const a = groupes[i];
    const b = groupes[i + 1];
    if (a && b && debut(a) + a.n * g > debut(b) + 1e-6) {
      groupes.splice(i, 2, { n: a.n + b.n, somme: a.somme + b.somme });
      i = Math.max(0, i - 1);
    } else i++;
  }
  return groupes.flatMap((gr) => Array.from({ length: gr.n }, (_, k) => debut(gr) + k * g));
}

/** Pose la ligne de temps dans la page ; rend la fonction qui l'en retire. */
export function monterLigneDeTemps(): () => void {
  const sections = [...document.querySelectorAll<HTMLElement>('[data-nav]')];
  const emploisPage = [...document.querySelectorAll<HTMLElement>('[data-annee][data-entreprise]')];
  const [premiere] = sections;
  const derniere = sections.at(-1);
  if (!premiere || !derniere) return () => undefined;

  const arret = new AbortController();
  const { signal } = arret;

  // L'étiquette répète ce que le curseur annonce par aria-valuetext : elle est cachée aux lecteurs
  // d'écran.
  const barre = element('nav', 'scrub');
  barre.setAttribute('aria-label', 'Parcours');
  const etiquette = element('p', 'scrub__label');
  etiquette.setAttribute('aria-hidden', 'true');
  const pastille = element('i', 'scrub__chip');
  const etiquetteAnnee = element('b', 'scrub__year');
  const depuis = element('small', 'scrub__since');
  depuis.textContent = 'depuis';
  const annee = document.createTextNode('');
  etiquetteAnnee.append(depuis, annee);
  const etiquetteNom = element('span', 'scrub__name');
  etiquette.append(pastille, etiquetteAnnee, etiquetteNom);

  const curseur = element('div', 'scrub__slider');
  curseur.setAttribute('role', 'slider');
  curseur.setAttribute('aria-label', 'Ligne de temps du parcours');
  curseur.setAttribute('aria-valuemin', '0');
  curseur.setAttribute('aria-valuemax', '100');
  curseur.setAttribute('aria-valuenow', '0');
  curseur.tabIndex = 0;
  const piste = element('div', 'scrub__track');
  const tete = element('div', 'scrub__head');
  curseur.append(piste, tete);
  barre.append(etiquette, curseur);
  document.body.append(barre);

  let raf = 0;
  const auDefilement = () => {
    if (!raf)
      raf = requestAnimationFrame(() => {
        raf = 0;
        actualiser();
      });
  };
  const rafraichir = () => {
    construire();
    actualiser();
  };
  addEventListener('scroll', auDefilement, { passive: true, signal });
  addEventListener('resize', rafraichir, { signal });
  addEventListener('load', rafraichir, { signal });
  void document.fonts.ready.then(() => {
    if (!signal.aborted) rafraichir();
  });
  // La page peut changer de hauteur sans que la fenêtre change : une image, ou un module qui allonge
  // les sections (les espaces de fin de l'empilement). Les positions se mesurent à nouveau.
  const observateur = new ResizeObserver(rafraichir);
  observateur.observe(document.body);
  signal.addEventListener('abort', () => {
    observateur.disconnect();
  });

  const hauteurDefilable = () => Math.max(1, document.documentElement.scrollHeight - innerHeight);

  // Point de lecture : à 40 % de l'écran, sauf dans le dernier écran de défilement, où il descend
  // jusqu'au bas. Ainsi la tête atteint le bout de la ligne et la dernière section (Contact)
  // devient courante.
  const lecture = () => {
    const reste = Math.min(1, (hauteurDefilable() - scrollY) / innerHeight);
    return scrollY + innerHeight * (1 - 0.6 * Math.max(0, reste));
  };
  // Haut de chaque section dans le flux, mesuré par construire().
  let hauts = new Map<HTMLElement, number>();
  const haut = (s: HTMLElement) => hauts.get(s) ?? s.offsetTop;
  const courante = (pos: number) => {
    let c = premiere;
    for (const s of sections) if (haut(s) <= pos + 2) c = s;
    return c;
  };
  // Inverse de lecture() : le défilement qui met le point de lecture sur la position p de la page.
  const allerA = (p: number) => {
    const M = hauteurDefilable();
    let y = p - innerHeight * 0.4;
    if (y > M - innerHeight) y = (p - innerHeight + 0.6 * M) / 1.6;
    window.scrollTo({ top: Math.min(M, Math.max(0, y)), behavior: 'instant' });
  };

  // La ligne suit la page en proportion, sauf là où des débuts d'emploi sont trop serrés pour
  // qu'on distingue leurs points (les studios, sur téléphone) : la ligne s'y dilate juste assez
  // pour les écarter de ECART pixels, et se resserre ailleurs. La carte porte les repères [page,
  // ligne] ; segments, années, points et tête passent tous par elle, et le glissement par son
  // inverse, donc la tête tombe toujours pile sur le point de l'emploi qu'elle annonce. Quand rien
  // ne se chevauche (ordinateur), la carte est l'identité.
  let carte: Repere[] = [
    [0, 0],
    [1, 1],
  ];
  const interpoler = (de: 0 | 1, vers: 0 | 1, v: number) => {
    let p: Repere | undefined;
    for (const q of carte) {
      if (p && v <= q[de])
        return p[vers] + ((v - p[de]) / (q[de] - p[de] || 1)) * (q[vers] - p[vers]);
      p = q;
    }
    return p ? p[vers] : v;
  };
  const versLigne = (f: number) => interpoler(0, 1, Math.min(1, Math.max(0, f)));
  const versPage = (x: number) => interpoler(1, 0, Math.min(1, Math.max(0, x)));

  let debuts = new Map<HTMLElement, Debut[]>();
  let rails = new Map<HTMLElement, string>();

  function construire() {
    piste.replaceChildren();
    debuts = new Map();
    rails = new Map(
      sections.map((s) => [s, getComputedStyle(s).getPropertyValue('--rail').trim()]),
    );
    const html = document.documentElement;
    html.classList.add('mesure-flux');
    const H = html.scrollHeight;
    const L = piste.getBoundingClientRect().width || 1;
    hauts = new Map(sections.map((s) => [s, s.offsetTop]));
    const emplois = emploisPage
      .map((el) => ({ el, top: el.getBoundingClientRect().top + scrollY }))
      .sort((u, v) => u.top - v.top);
    html.classList.remove('mesure-flux');
    const fractions = emplois.map((e) => e.top / H);
    const xs = ecarter(
      fractions.map((f) => f * L),
      ECART,
      L,
    );
    // Sans chevauchement, les positions reviennent inchangées : on garde l'identité, au pixel près.
    const ecartes = fractions.some((f, i) => Math.abs(f * L - (xs[i] ?? f * L)) >= 0.5);
    carte = ecartes
      ? [[0, 0], ...fractions.map((f, i): Repere => [f, (xs[i] ?? f * L) / L]), [1, 1]]
      : [
          [0, 0],
          [1, 1],
        ];

    for (const s of sections) {
      const a = versLigne(haut(s) / H);
      const b = versLigne((haut(s) + s.offsetHeight) / H);
      const segment = element('i', 'scrub__seg');
      segment.style.left = `${a * 100}%`;
      segment.style.width = `${(b - a) * 100}%`;
      segment.style.background = rails.get(s) ?? '';
      piste.append(segment);
      if (s.dataset.chapitre !== undefined && s.dataset.y0) {
        const etiquetteChapitre = element('span', 'scrub__tick');
        etiquetteChapitre.style.left = `${a * 100}%`;
        etiquetteChapitre.textContent =
          s.dataset.parallele !== undefined ? `depuis ${s.dataset.y0}` : s.dataset.y0;
        piste.append(etiquetteChapitre);
      }
    }
    for (const { el, top } of emplois) {
      const { annee: an = '', entreprise = '', poste = '', court } = el.dataset;
      const chapitre = el.closest<HTMLElement>('[data-nav]');
      if (chapitre)
        debuts.set(chapitre, [...(debuts.get(chapitre) ?? []), [top, an, court || entreprise]]);
      const point = element('i', 'scrub__dot');
      point.dataset.tip = `${an} · ${entreprise}, ${poste}`;
      point.dataset.top = String(top);
      point.style.left = `${versLigne(top / H) * 100}%`;
      piste.append(point);
    }
  }

  let affiche = '';

  function actualiser() {
    // Toutes les lectures de mise en page d'abord, puis les écritures.
    const H = document.documentElement.scrollHeight;
    const pos = lecture();
    const c = courante(pos);
    const largeur = piste.clientWidth;
    const f = Math.min(1, Math.max(0, pos / H));
    const debut = (debuts.get(c) ?? []).filter(([p]) => p <= pos + 2).at(-1);
    const an = debut ? debut[1] : (c.dataset.y0 ?? '');
    const nom = debut ? debut[2] : (c.dataset.nav ?? '');
    const parallele = Boolean(an) && c.dataset.parallele !== undefined;

    tete.style.transform = `translateX(${versLigne(f) * largeur}px)`;
    barre.classList.toggle('is-in', scrollY > innerHeight * 0.3);
    curseur.setAttribute('aria-valuenow', String(Math.round(f * 100)));

    const texte = [parallele ? `depuis ${an}` : an, nom].filter(Boolean).join(', ');
    if (texte === affiche) return;
    affiche = texte;
    barre.style.setProperty('--hc', rails.get(c) ?? '');
    depuis.hidden = !parallele;
    annee.data = an;
    etiquetteAnnee.hidden = !an;
    etiquetteNom.textContent = nom;
    curseur.setAttribute('aria-valuetext', texte);
  }

  const glisserVers = (x: number) => {
    const t = piste.getBoundingClientRect();
    allerA(versPage((x - t.left) / t.width) * document.documentElement.scrollHeight);
  };

  // Un clic sur un point mène pile au début de l'emploi ; ailleurs, la ligne se glisse comme une
  // vidéo. Au doigt, glisser sur la ligne s'arrête aussi sur chaque point qu'on croise, pour qu'on
  // puisse choisir un emploi sans viser un point de 11 pixels.
  const pointPres = (x: number, rayon: number) => {
    let meilleur: HTMLElement | undefined;
    let distance = rayon;
    for (const d of piste.querySelectorAll<HTMLElement>('.scrub__dot')) {
      const r = d.getBoundingClientRect();
      const k = Math.abs(r.left + r.width / 2 - x);
      if (k < distance) {
        distance = k;
        meilleur = d;
      }
    }
    return meilleur;
  };
  const allerAuPoint = (point: HTMLElement) => {
    allerA(Number(point.dataset.top) + 1);
  };

  let x0 = 0;
  let doigt = false;
  curseur.addEventListener(
    'pointerdown',
    (e) => {
      doigt = e.pointerType !== 'mouse';
      const point = pointPres(e.clientX, doigt ? ECART / 2 : 8);
      curseur.setPointerCapture(e.pointerId);
      curseur.classList.add('is-drag');
      x0 = e.clientX;
      if (point) allerAuPoint(point);
      else glisserVers(e.clientX);
    },
    { signal },
  );
  curseur.addEventListener(
    'pointermove',
    (e) => {
      if (!curseur.classList.contains('is-drag') || Math.abs(e.clientX - x0) <= 3) return;
      x0 = -1e9;
      const point = doigt ? pointPres(e.clientX, ECART / 2) : undefined;
      if (point) allerAuPoint(point);
      else glisserVers(e.clientX);
    },
    { signal },
  );
  for (const type of ['pointerup', 'pointercancel'] as const)
    curseur.addEventListener(
      type,
      () => {
        curseur.classList.remove('is-drag');
      },
      { signal },
    );

  // Clavier : les flèches passent d'une section à l'autre, Début et Fin vont aux deux bouts. On
  // vise le point de lecture, pas le haut de l'écran, pour que la section visée devienne courante.
  curseur.addEventListener(
    'keydown',
    (e) => {
      const i = sections.indexOf(courante(lecture()));
      const cible =
        e.key === 'ArrowRight'
          ? sections[i + 1]
          : e.key === 'ArrowLeft'
            ? sections[i - 1]
            : e.key === 'Home'
              ? premiere
              : e.key === 'End'
                ? derniere
                : undefined;
      if (!cible) return;
      e.preventDefault();
      if (cible === premiere) window.scrollTo({ top: 0, behavior: 'instant' });
      else allerA(haut(cible) + 1);
    },
    { signal },
  );

  rafraichir();

  return () => {
    arret.abort();
    cancelAnimationFrame(raf);
    barre.remove();
  };
}
